import { getTOtp, syncConnections } from "@/api/rest/authenticated";
import Loading from "@/components/Loading";
import { AuthSignUpPageContainer } from "@/components/pages/Auth/AuthSignUpPageOutlet";
import { useAppSelector } from "@/redux/hooks";
import { getAllCarts } from "@/redux/selectors";
import { Retailer } from "@/redux/types";
import {
  getPurchaseRewardAmountForProductV2,
  MOBILE_SCREEN_SIZE,
} from "@brandclub/common-ui";
import { Box, IconButton, InputAdornment, useMediaQuery } from "@mui/material";
import CryptoJS from "crypto-js";
import { isNil } from "lodash";
import _isEqual from "lodash/isEqual";
import md5 from "md5";
import { useEffect, useState } from "react";
import { CheckoutInputV2 } from "../../../CheckoutInputV2";
import {
  AccountConnectBenefits,
  AccountConnectRewards,
  RewardExpandContainer,
} from "../../../CheckoutSummary/components/AccountRewards";
import { useCheckoutUI } from "../../CheckoutUIProvider";
import { useCheckoutRetailer } from "../../hooks/useCheckoutRetailer";
import { CheckoutButton } from "./CheckoutButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

const ROW_SPACING = 25;

export const useInjectRetailerCredentials = () => {
  const [state] = useCheckoutUI();

  const retailers = useAppSelector((state) => state.retailers);
  const currentRetailer = retailers.find(
    (x) => x.retailerId === state.context.checkoutIntent?.retailerId
  );
  const [username, setUsername] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const { token, currentStamp } = await getTOtp();
        const accountsDataEncrypted = await syncConnections({
          currentStamp,
          shouldFetchAllConnectionStatuses: true,
        });
        const accounts = JSON.parse(
          CryptoJS.AES.decrypt(accountsDataEncrypted, token).toString(
            CryptoJS.enc.Utf8
          )
        );

        const account = accounts?.find(
          (x: any) => x.retailerId === currentRetailer?.parentRetailerId
        );

        if (account && currentRetailer?.retailerId) {
          setUsername(account.credential.username);
          setPassword(account.credential.password);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    })();
  }, [currentRetailer]);

  return { username, password, loading };
};

export const CheckoutConnectRetailer = ({
  onSaveCredentials,
  submitting = false,
  usernameFromCheckoutIntent,
  passwordHashFromCheckoutIntent,
  buttonChildren = "Connect",
}: {
  onSaveCredentials: (username: string, password: string) => Promise<void>;
  submitting?: boolean;
  usernameFromCheckoutIntent?: string;
  passwordHashFromCheckoutIntent?: string;
  buttonChildren?: string | ((props: { edited: boolean }) => React.ReactNode);
}) => {
  const {
    username: usernameFromState,
    password: decryptedPassword,
    loading,
  } = useInjectRetailerCredentials();

  const [state, send] = useCheckoutUI();

  const allCarts = useAppSelector(getAllCarts, _isEqual);
  const cartProducts = useAppSelector((state) => state.checkout.products);

  const { checkoutIntent } = state.context;

  const isMobile = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);

  const [username, setUsername] = useState<string>(
    usernameFromCheckoutIntent ?? usernameFromState ?? ""
  );
  const [password, setPassword] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState<boolean>(false);

  useEffect(() => {
    // Set the default password to the decrypted password if it matches the hash
    if (!loading) {
      const credentialsMatch =
        username === usernameFromCheckoutIntent &&
        md5(password ?? "") === passwordHashFromCheckoutIntent;

      if (!credentialsMatch) {
        setPassword(decryptedPassword);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decryptedPassword]);

  const edited =
    !loading &&
    (username !== usernameFromCheckoutIntent ||
      md5(password ?? "") !== passwordHashFromCheckoutIntent);

  useEffect(() => {
    if (!loading && !isNil(password)) {
      // if the password is set from auto or user inpput
      if (edited) {
        send({
          type: "CHANGE_RETAILER_LOGIN_COMPLETE_STATUS",
          data: { forceComplete: true },
        });
      } else {
        send({
          type: "CHANGE_RETAILER_LOGIN_COMPLETE_STATUS",
          data: { forceComplete: false },
        });
      }
    }
  }, [edited, loading, password, send]);

  const getUserNameFieldHeading = (retailer: Retailer) => {
    if (retailer.retailerLoginUsernameFieldTypes?.length) {
      const supportedFieldTypes = retailer.retailerLoginUsernameFieldTypes;
      if (
        supportedFieldTypes.includes("email") &&
        supportedFieldTypes.includes("phoneNumber")
      ) {
        return "Email or mobile phone";
      }
    }
    return "Email";
  };

  const { currentRetailer: retailer } = useCheckoutRetailer();

  const currentCart = allCarts.find(
    (x) => x.retailerId === retailer?.retailerId
  );

  const currentCartProducts = currentCart?.items.map((x) => {
    return cartProducts?.[x.stacklineSku];
  });

  const purchaseReward =
    currentCartProducts?.reduce((acc, product) => {
      const { amount } = getPurchaseRewardAmountForProductV2(product);
      return acc + amount;
    }, 0) ?? 0;
  const connectReward = retailer?.retailerConnectRewardAmount ?? 0;
  const membershipReward = 0;

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  if (!checkoutIntent) {
    return null;
  }

  return (
    <AuthSignUpPageContainer>
      {!retailer ? (
        <div className="page_title">
          <Loading dot />
        </div>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "column", md: "row" },
              gap: "16.3px",
              width: "100%",
            }}
          >
            <CheckoutInputV2
              label={getUserNameFieldHeading(retailer)}
              inputStyle={{ width: "100%" }}
              style={{ marginTop: ROW_SPACING }}
              onChange={(e) => {
                setUsername(e.target.value as any);
              }}
              sx={{ maxWidth: "100%" }}
              disabled={submitting}
              value={username}
              InputProps={{
                autoComplete: "username",
                inputMode: "email",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
              }}
            />
            <CheckoutInputV2
              label="Password"
              type={showPassword ? "text" : "password"}
              inputStyle={{ width: "100%" }}
              style={{ marginTop: ROW_SPACING }}
              onChange={(e) => {
                setPassword(e.target.value as any);
              }}
              sx={{ maxWidth: "100%" }}
              disabled={submitting}
              value={password}
              InputProps={{
                autoComplete: "current-password",
                name: "password-checkout-" + Math.random(),
                inputMode: "text",
                autoCapitalize: "none",
                autoCorrect: "off",
                spellCheck: false,
                endAdornment: isMobile ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                      disabled={submitting}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              endButton={
                isMobile ? null : (
                  <Box display="flex" alignItems="center">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      size="small"
                      disabled={submitting}
                      sx={{ mr: 1 }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                    <CheckoutButton
                      onClick={async () => {
                        await onSaveCredentials(username, password ?? "");
                      }}
                      disabled={submitting || !username || !password || !edited}
                    >
                      Connect
                    </CheckoutButton>
                  </Box>
                )
              }
            />
          </Box>
          {isMobile ? (
            <CheckoutButton
              style={{ width: "100%", marginTop: "19.5px" }}
              onClick={async () => {
                await onSaveCredentials(username, password ?? "");
              }}
              disabled={submitting || !username || !password || !edited}
            >
              {typeof buttonChildren === "function"
                ? buttonChildren({ edited })
                : buttonChildren}
            </CheckoutButton>
          ) : null}
          <RewardExpandContainer>
            <AccountConnectRewards
              retailerId={retailer.retailerId}
              purchaseAmount={purchaseReward}
              connectAmount={connectReward}
              membershipAmount={membershipReward}
            />
            <AccountConnectBenefits retailerId={retailer.retailerId} />
          </RewardExpandContainer>
        </>
      )}
    </AuthSignUpPageContainer>
  );
};
